import React, { useState, useEffect } from "react";
import { ShowButton } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import { Table, Space, Card, Input } from "antd";
import { SearchOutlined, BarChartOutlined } from "@ant-design/icons";
import { PageHeader } from "../../../components/ui";

interface PeriodBalance {
    id: number;
    accountingPeriodId: number;
    accountCode: string;
    accountName?: string;
    openingBalance: number;
    debitTotal: number;
    creditTotal: number;
    closingBalance: number;
    lastUpdated: string;
    createdAt: string;
    accountingPeriod?: { periodId: string };
    account?: { name: string };
}

export const PeriodBalancesList = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [filteredItems, setFilteredItems] = useState<PeriodBalance[]>([]);

    const { data, isLoading } = useList({
        resource: "period-balances",
    });

    useEffect(() => {
        if (!data?.data) return;

        let balances = data.data as PeriodBalance[];

        if (searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            balances = balances.filter((balance: PeriodBalance) =>
                balance.accountCode?.toLowerCase().includes(searchLower) ||
                balance.accountName?.toLowerCase().includes(searchLower) ||
                balance.account?.name?.toLowerCase().includes(searchLower)
            );
        }

        const sorted = [...balances].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setFilteredItems(sorted);
    }, [data, searchText]);

    const columns = [
        {
            title: "Period",
            key: "period",
            render: (_: any, record: PeriodBalance) => (
                <span className="font-medium text-text-primary">
                    {record.accountingPeriod?.periodId || record.accountingPeriodId}
                </span>
            ),
        },
        {
            title: "Account Code",
            dataIndex: "accountCode",
            key: "accountCode",
            render: (value: string) => (
                <span className="text-text-secondary">{value}</span>
            ),
        },
        {
            title: "Account",
            key: "accountName",
            render: (_: any, record: PeriodBalance) => (
                <span className="text-text-secondary">
                    {record.account?.name || record.accountName || "-"}
                </span>
            ),
            responsive: ['md'] as any,
        },
        {
            title: "Opening",
            dataIndex: "openingBalance",
            key: "openingBalance",
            render: (value: number) => (
                <span className="text-text-secondary">${value?.toLocaleString()}</span>
            ),
            align: "right" as any,
            responsive: ['lg'] as any,
        },
        {
            title: "Debits",
            dataIndex: "debitTotal",
            key: "debitTotal",
            render: (value: number) => (
                <span className="text-success">${value?.toLocaleString()}</span>
            ),
            align: "right" as any,
        },
        {
            title: "Credits",
            dataIndex: "creditTotal",
            key: "creditTotal",
            render: (value: number) => (
                <span className="text-danger">${value?.toLocaleString()}</span>
            ),
            align: "right" as any,
        },
        {
            title: "Closing",
            dataIndex: "closingBalance",
            key: "closingBalance",
            render: (value: number) => (
                <span className="font-semibold text-primary">${value?.toLocaleString()}</span>
            ),
            align: "right" as any,
        },
        {
            title: "Updated",
            dataIndex: "lastUpdated",
            key: "lastUpdated",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
            responsive: ['md'] as any,
        },
        {
            title: "Actions",
            key: "actions",
            width: 80,
            render: (_: any, record: PeriodBalance) => (
                <Space size="small">
                    <ShowButton hideText size="small" recordItemId={record.id} />
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Period Balances"
                icon={<BarChartOutlined />}
                breadcrumbs={[{ title: "Accounting" }, { title: "Period Balances" }]}
            />

            <div className="data-table-card">
                <Card>
                    <div style={{ marginBottom: 16 }}>
                        <Input
                            placeholder="Search by account code or name"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 400 }}
                            allowClear
                            size="large"
                        />
                    </div>

                    <Table
                        dataSource={filteredItems}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            pageSize: 15,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        className="custom-table"
                        scroll={{ x: 1000 }}
                    />
                </Card>
            </div>
        </div>
    );
};
